/*
 * Copyright (c) 2025 by tibetiroka.
 *
 * endless-sky-wiki-generator is free software: you can redistribute it and/or modify it under the terms of the GNU General Public License as published by the Free Software Foundation, either version 3 of the License, or (at your option) any later version.
 *
 * endless-sky-wiki-generator is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {ReferenceSource} from './data/ReferenceSource.ts';
const LZString = require('lz-string');

export const HOME_PATH: string = 'endless-sky-wiki-generator';
export const HOST_ORIGIN: string = 'tibetiroka.github.io';
export const DATA_ORIGIN: string = isHostedOnLocalhost() ? 'http://localhost:8080/'
	: 'https://raw.githubusercontent.com/tibetiroka/' + HOME_PATH + '/refs/heads/deploy/';

export function isHostedOnLocalhost(hostname: string = window.location.hostname): boolean {
	return hostname === 'localhost';
}

export function isHostedOn(domain: string, hostname: string = window.location.hostname, allowSubdomain: boolean = false): boolean {
	if (hostname === domain) {
		return true;
	} else {
		return allowSubdomain && hostname.endsWith('.' + domain);
	}
}

export function getDomainWithProtocol(protocol: string = window.location.protocol, baseDomain: string = window.location.hostname): string {
	return (protocol.endsWith(':') ? protocol : protocol + ':') + '//' + baseDomain + (window.location.port.length > 0 ? ':' + window.location.port : '')
}

export function getPath(relativeTo: string = '', pathname: string = decodeURIComponent(window.location.pathname)): string {
	if (pathname.startsWith('/')) {
		return pathname.substring(relativeTo.length + (relativeTo.startsWith('/') ? 0 : 1));
	}
	// empty path
	return '';
}

export function createPath(path: string, relativeTo: string = getDomainWithProtocol() + '/' + HOME_PATH + '/'): URL {
	return new URL(relativeTo + path);
}

export function getCurrentSource(relativeTo: string = HOME_PATH, pathname: string = decodeURIComponent(window.location.pathname)): ReferenceSource {
	let path: string = getPath(relativeTo, pathname);
	path = path.replace(/^\/+/, '').replace(/\/+$/, '');
	const parts: string[] = path.split('/').filter(s=>s.length > 0);
	if (parts.length === 0) {
		return new ReferenceSource('main', null);
	} else if (parts.length === 1) {
		return new ReferenceSource(parts[0], null);
	} else {
		return new ReferenceSource(parts[0], parts[1]);
	}
}

export function isOfficial(href: string = window.location.href): boolean {
	if (href === 'localhost' || href.startsWith('/')) {
		return true;
	}
	href = new URL(href).hostname;
	return isHostedOnLocalhost(href) || isHostedOn(HOST_ORIGIN, href);
}

export function getDataUrl(path: string): URL {
	return new URL(DATA_ORIGIN.toString() + encodeURIComponent(path));
}

export function fetchData(path: string, cacheLifeMs: number = 1000 * 60 * 60 * 5): Promise<string> {
	const url: URL = getDataUrl(path);
	const cacheDate = localStorage.getItem(path + '|date');
	// check if there is a cached value
	if (cacheLifeMs > 0 && cacheDate !== null) {
		const date = Number.parseInt(cacheDate);
		const age = Date.now() - date;
		if (age < cacheLifeMs) {
			const cacheData = localStorage.getItem(path);
			const compressedCacheData = localStorage.getItem(path + '|compressed');
			if (cacheData !== null && cacheData !== '') {
				return Promise.resolve(cacheData);
			} else if(compressedCacheData !== null && compressedCacheData !== '') {
				return Promise.resolve(LZString.decompress(compressedCacheData));
			} else {
				return Promise.reject('Cached data is marked as invalid');
			}
		}
	}
	return fetch(url).then(response => {
		const decompressed = response.body?.pipeThrough(new DecompressionStream('gzip'));
		return new Response(decompressed).text();
	}).then(text => {
		if (cacheLifeMs > 0) {
			localStorage.setItem(path + '|date', Date.now().toString());
			localStorage.setItem(path + '|life', cacheLifeMs.toString());
			localStorage.setItem(path + '|compressed', LZString.compress(text));
		}
		return Promise.resolve(text);
	}).catch(reason => {
		localStorage.setItem(path + '|date', Date.now().toString());
		localStorage.setItem(path + '|life', (1000 * 60 * 5).toString());
		localStorage.setItem(path + '|compressed', '');
		localStorage.setItem(path, '');
		return reason;
	});
}

function clearCache() {
	for (const key in localStorage) {
		if (key.endsWith('|date')) {
			const rawKey = key.substring(0, key.length - '|date'.length);
			const date = Number.parseInt(localStorage.getItem(rawKey + '|date') ?? '0');
			const life = Number.parseInt(localStorage.getItem(rawKey + '|life') ?? '0');
			if (Date.now() - date >= life) {
				localStorage.removeItem(rawKey);
				localStorage.removeItem(rawKey + '|compressed')
				localStorage.removeItem(rawKey + '|date');
				localStorage.removeItem(rawKey + '|life');
			}
		}
	}
}

clearCache();
